import { Building2, Mail, Plus, User } from "lucide-react";
import { Link } from "react-router-dom";

export type ConnectedRecordIcon = "building" | "user" | "mail";

export type ConnectedRecord = {
  id: string;
  label: string;
  href: string;
  meta?: string;
  icon?: ConnectedRecordIcon;
};

type ConnectedRecordsPanelProps = {
  accounts?: ConnectedRecord[];
  contacts?: ConnectedRecord[];
  deals?: ConnectedRecord[];
  leads?: ConnectedRecord[];
  emails?: ConnectedRecord[];
  /** Overrides the Accounts section title. */
  accountsTitle?: string;
  /** Overrides the Contacts section title. */
  contactsTitle?: string;
  /** Highlights the row whose `id` matches (case-insensitive). */
  selectedRecordId?: string | null;
  createContactHref?: string;
  createDealHref?: string;
  createAccountHref?: string;
  createLeadHref?: string;
  hideEmptyLeads?: boolean;
  compact?: boolean;
};

function recordIsSelected(recordId: string, selectedRecordId?: string | null): boolean {
  if (!selectedRecordId?.trim()) return false;
  return recordId.trim().toLowerCase() === selectedRecordId.trim().toLowerCase();
}

function ConnectedSection({
  title,
  records,
  createHref,
  createLabel,
  compact = false,
  selectedRecordId,
  emptyMessage,
}: {
  title: string;
  records: ConnectedRecord[];
  createHref?: string;
  createLabel?: string;
  compact?: boolean;
  selectedRecordId?: string | null;
  emptyMessage?: string;
}) {
  const addLabel = createLabel || `Add ${title.slice(0, -1)}`;
  const emptyCopy = emptyMessage ?? (createHref ? "None yet. Use Add to create one." : "No linked records.");

  const RecordIcon = ({ icon }: { icon?: ConnectedRecordIcon }) => {
    if (icon === "building") {
      return <Building2 className={compact ? "size-3 shrink-0 text-gray-500" : "size-3.5 shrink-0 text-gray-500"} aria-hidden />;
    }
    if (icon === "user") {
      return <User className={compact ? "size-3 shrink-0 text-gray-500" : "size-3.5 shrink-0 text-gray-500"} aria-hidden />;
    }
    if (icon === "mail") {
      return <Mail className={compact ? "size-3 shrink-0 text-gray-500" : "size-3.5 shrink-0 text-gray-500"} aria-hidden />;
    }
    return null;
  };

  return (
    <section
      className={
        compact
          ? "rounded-md border border-gray-200 p-2 dark:border-white/10"
          : "space-y-2 rounded-md border border-gray-200 p-3 dark:border-white/10"
      }
    >
      <div className={compact ? "mb-1 flex items-center justify-between gap-2" : "flex items-center justify-between gap-2"}>
        <div className="flex min-w-0 items-center gap-2">
          <h3 className={compact ? "text-xs font-semibold" : "text-sm font-semibold"}>{title}</h3>
          {compact ? (
            <span className="text-[11px] text-gray-500 dark:text-gray-400">{records.length}</span>
          ) : null}
        </div>
        {createHref ? (
          <Link
            to={createHref}
            className={
              compact
                ? "inline-flex items-center gap-0.5 rounded px-1 py-0.5 text-[11px] font-medium text-indigo-600 hover:bg-indigo-50 dark:text-indigo-300 dark:hover:bg-indigo-950/40"
                : "inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-indigo-600 hover:bg-indigo-50 dark:text-indigo-300 dark:hover:bg-indigo-950/40"
            }
            title={addLabel}
            aria-label={addLabel}
          >
            <Plus className={compact ? "size-3" : "size-3.5"} aria-hidden />
            <span>{compact ? "Add" : addLabel}</span>
          </Link>
        ) : null}
      </div>
      {records.length === 0 ? (
        <p className={compact ? "text-[11px] text-gray-500 dark:text-gray-400" : "text-xs text-gray-500 dark:text-gray-400"}>
          {emptyCopy}
        </p>
      ) : (
        <ul className="space-y-1">
          {records.slice(0, compact ? 6 : undefined).map((record) => {
            const selected = recordIsSelected(record.id, selectedRecordId);
            return (
              <li key={`${title}-${record.id}`}>
                <Link
                  to={record.href}
                  aria-current={selected ? "page" : undefined}
                  className={
                    compact
                      ? `flex items-center gap-1.5 rounded px-1.5 py-1 text-xs font-medium ${
                          selected
                            ? "border border-indigo-400 bg-indigo-50 text-indigo-900 dark:border-indigo-400/60 dark:bg-indigo-900/30 dark:text-indigo-100"
                            : "hover:bg-gray-50 dark:hover:bg-white/10"
                        }`
                      : `flex items-start gap-2 rounded px-2 py-1 text-sm ${
                          selected
                            ? "border border-indigo-400 bg-indigo-50 dark:border-indigo-400/60 dark:bg-indigo-900/30"
                            : "hover:bg-gray-50 dark:hover:bg-white/10"
                        }`
                  }
                >
                  {compact ? (
                    <>
                      <RecordIcon icon={record.icon} />
                      <span className="min-w-0 truncate">{record.label}</span>
                    </>
                  ) : (
                    <>
                      <RecordIcon icon={record.icon} />
                      <span className="min-w-0">
                        <p className="font-medium">{record.label}</p>
                        {record.meta ? <p className="text-xs text-gray-500 dark:text-gray-400">{record.meta}</p> : null}
                      </span>
                    </>
                  )}
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

export function ConnectedRecordsPanel({
  accounts = [],
  contacts = [],
  deals = [],
  leads = [],
  emails = [],
  accountsTitle = "Accounts",
  contactsTitle = "Contacts",
  selectedRecordId,
  createContactHref,
  createDealHref,
  createAccountHref,
  createLeadHref,
  hideEmptyLeads = false,
  compact = false,
}: ConnectedRecordsPanelProps) {
  const sections = [
    {
      key: "accounts",
      title: accountsTitle,
      records: accounts,
      createHref: createAccountHref,
      createLabel: "Add account",
      hideWhenEmpty: false,
      emptyMessage: undefined as string | undefined,
    },
    {
      key: "contacts",
      title: contactsTitle,
      records: contacts,
      createHref: createContactHref,
      createLabel: "Add contact",
      hideWhenEmpty: false,
      emptyMessage: undefined,
    },
    {
      key: "deals",
      title: "Deals",
      records: deals,
      createHref: createDealHref,
      createLabel: "Add deal",
      hideWhenEmpty: false,
      emptyMessage: undefined,
    },
    {
      key: "leads",
      title: "Leads",
      records: leads,
      createHref: createLeadHref,
      createLabel: "Add lead",
      hideWhenEmpty: hideEmptyLeads,
      emptyMessage: undefined,
    },
    {
      key: "emails",
      title: "Emails",
      records: emails,
      createHref: undefined,
      createLabel: undefined,
      hideWhenEmpty: false,
      emptyMessage: "No linked emails yet.",
    },
  ].filter((section) => {
    if (!section.hideWhenEmpty) return true;
    return section.records.length > 0 || Boolean(section.createHref);
  });

  if (compact) {
    return (
      <aside className="space-y-3 rounded-lg border border-gray-200 bg-white p-3 dark:border-white/10 dark:bg-white/5">
        <div>
          <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-700 dark:text-gray-200">
            Connected Records
          </h2>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Quick links across CRM modules.</p>
        </div>
        <div className="space-y-2">
          {sections.map((section) => (
            <ConnectedSection
              key={section.key}
              title={section.title}
              records={section.records}
              createHref={section.createHref}
              createLabel={section.createLabel}
              emptyMessage={section.emptyMessage}
              selectedRecordId={selectedRecordId}
              compact
            />
          ))}
        </div>
      </aside>
    );
  }

  return (
    <section className="space-y-3 rounded-lg border border-gray-200 bg-white p-4 dark:border-white/10 dark:bg-white/5">
      <div>
        <h2 className="text-lg font-semibold">Connected Records</h2>
        <p className="text-sm text-gray-600 dark:text-gray-300">
          Navigate related accounts, contacts, deals, leads, and emails for this record.
        </p>
      </div>
      <div className="grid gap-3 md:grid-cols-2">
        {sections.map((section) => (
          <ConnectedSection
            key={section.key}
            title={section.title}
            records={section.records}
            createHref={section.createHref}
            createLabel={section.createLabel}
            emptyMessage={section.emptyMessage}
            selectedRecordId={selectedRecordId}
          />
        ))}
      </div>
    </section>
  );
}
